/**
 * SBOMViewer Component
 *
 * Display Software Bill of Materials with search and export
 */

import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Package, Download, Search, ExternalLink } from 'lucide-react';
import type { SBOM } from '@forgenetwork/types';

interface SBOMViewerProps {
  sbom: SBOM;
}

/**
 * Export SBOM to JSON
 */
function downloadJSON(sbom: SBOM, filename: string) {
  const blob = new Blob([JSON.stringify(sbom, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Export SBOM to XML (CycloneDX format)
 */
function downloadXML(sbom: SBOM, filename: string) {
  // Simple XML generation for CycloneDX format
  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += `<bom xmlns="http://cyclonedx.org/schema/bom/${sbom.specVersion}" version="${sbom.version}">\n`;
  xml += '  <components>\n';

  sbom.components.forEach((component) => {
    xml += `    <component type="${component.type}">\n`;
    xml += `      <name>${component.name}</name>\n`;
    xml += `      <version>${component.version}</version>\n`;
    if (component.purl) {
      xml += `      <purl>${component.purl}</purl>\n`;
    }
    if (component.description) {
      xml += `      <description>${component.description}</description>\n`;
    }
    if (component.licenses && component.licenses.length > 0) {
      xml += '      <licenses>\n';
      component.licenses.forEach((license) => {
        xml += `        <license><id>${license}</id></license>\n`;
      });
      xml += '      </licenses>\n';
    }
    xml += '    </component>\n';
  });

  xml += '  </components>\n';
  xml += '</bom>';

  const blob = new Blob([xml], { type: 'application/xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * SBOMViewer - Display and export SBOM data
 */
export function SBOMViewer({ sbom }: SBOMViewerProps): React.JSX.Element {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [ecosystemFilter, setEcosystemFilter] = React.useState<string>('all');
  const [sortBy, setSortBy] = React.useState<'name' | 'type' | 'ecosystem'>(
    'name'
  );

  // Extract unique ecosystems
  const ecosystems = React.useMemo(() => {
    const unique = new Set<string>();
    sbom.components.forEach((c) => {
      if (c.ecosystem) unique.add(c.ecosystem);
    });
    return Array.from(unique).sort();
  }, [sbom.components]);

  // Filter and search components
  const filteredComponents = React.useMemo(() => {
    let filtered = sbom.components;

    // Filter by ecosystem
    if (ecosystemFilter !== 'all') {
      filtered = filtered.filter((c) => c.ecosystem === ecosystemFilter);
    }

    // Search by name or description
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.name.toLowerCase().includes(query) ||
          c.description?.toLowerCase().includes(query)
      );
    }

    // Sort
    const sorted = [...filtered];
    if (sortBy === 'name') {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'type') {
      sorted.sort((a, b) => a.type.localeCompare(b.type));
    } else if (sortBy === 'ecosystem') {
      sorted.sort((a, b) => {
        const aEco = a.ecosystem || '';
        const bEco = b.ecosystem || '';
        return aEco.localeCompare(bEco);
      });
    }

    return sorted;
  }, [sbom.components, searchQuery, ecosystemFilter, sortBy]);

  // Group by ecosystem for stats
  const ecosystemStats = React.useMemo(() => {
    const stats: Record<string, number> = {};
    sbom.components.forEach((c) => {
      const eco = c.ecosystem || 'unknown';
      stats[eco] = (stats[eco] || 0) + 1;
    });
    return stats;
  }, [sbom.components]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2 mb-2">
              <Package className="h-5 w-5" />
              Software Bill of Materials
            </CardTitle>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>
                Format: {sbom.bomFormat} v{sbom.specVersion}
              </span>
              <span>Components: {sbom.components.length}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                downloadJSON(sbom, `sbom-${Date.now()}.json`)
              }
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              JSON
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                downloadXML(sbom, `sbom-${Date.now()}.xml`)
              }
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              XML
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Ecosystem Stats */}
        <div className="flex items-center gap-2 flex-wrap">
          {Object.entries(ecosystemStats).map(([eco, count]) => (
            <Badge key={eco} variant="outline" className="gap-1">
              <span className="font-medium">{eco}</span>
              <span className="text-muted-foreground">({count})</span>
            </Badge>
          ))}
        </div>

        {/* Search and Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search components..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={ecosystemFilter} onValueChange={setEcosystemFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Ecosystems" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ecosystems</SelectItem>
              {ecosystems.map((eco) => (
                <SelectItem key={eco} value={eco}>
                  {eco} ({ecosystemStats[eco] || 0})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as 'name' | 'type' | 'ecosystem')}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Sort by Name</SelectItem>
              <SelectItem value="type">Sort by Type</SelectItem>
              <SelectItem value="ecosystem">Sort by Ecosystem</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Components Table */}
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Component</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Ecosystem</TableHead>
                <TableHead>License</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredComponents.length > 0 ? (
                filteredComponents.map((component, idx) => {
                  const npmUrl = component.ecosystem === 'npm'
                    ? `https://www.npmjs.com/package/${component.name}`
                    : null;

                  return (
                    <TableRow key={`${component.name}-${idx}`}>
                      <TableCell className="font-medium">
                        <div>
                          <div>{component.name}</div>
                          {component.description && (
                            <div className="text-xs text-muted-foreground line-clamp-1">
                              {component.description}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {component.type}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {component.version}
                      </TableCell>
                      <TableCell>
                        {component.ecosystem && (
                          <Badge variant="secondary" className="text-xs">
                            {component.ecosystem}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {component.licenses && component.licenses.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {component.licenses.slice(0, 2).map((license) => (
                              <Badge
                                key={license}
                                variant="outline"
                                className="text-xs"
                              >
                                {license}
                              </Badge>
                            ))}
                            {component.licenses.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{component.licenses.length - 2}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {npmUrl && (
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                            className="h-7 w-7 p-0"
                          >
                            <a
                              href={npmUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="View on npm"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="text-muted-foreground">
                      {searchQuery || ecosystemFilter !== 'all'
                        ? 'No components match your filters'
                        : 'No components found'}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Footer with PURL info */}
        <div className="text-xs text-muted-foreground">
          <p>
            Package URLs (PURL) provide a standardized way to identify software
            packages across ecosystems.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
